/**
 * How large the preview draws a page (part 15, table Z): as wide as the pane allows, never larger than the page itself
 * (an inch on the page is an inch on the screen), at the screen's own pixel density. The scale is rounded down to a grid
 * so a pane resized by a pixel or two doesn't redraw every page; on a pane too narrow for the smallest scale the worker
 * draws at, the page is that small and overflows it.
 */
import { MAX_SCALE, MIN_SCALE } from '@cockatrice/layout';
import type { BookMode, DeviceProfile } from '@cockatrice/layout';

/** CSS pixels a page keeps clear on each side in the pane. */
export const PAGE_GUTTER = 16;
/** CSS pixels per point: CSS fixes 96 pixels and 72 points to the inch. */
export const CSS_PER_POINT = 96 / 72;
/** Z3: scales are whole multiples of 1/64 of a pixel per point. */
export const SCALE_STEPS = 64;

/** A page as the pane shows it: the canvas pixels per point the worker draws at, and its size in CSS pixels. */
export interface PageScale {
  readonly scale: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Z1–Z5: the scale for a pane `paneWidth` CSS pixels wide on a screen of `ratio` device pixels per CSS pixel; null
 * while the pane has no width to draw in (Z4). A ratio that isn't a positive number counts as 1 (Z5).
 */
export function pageScale(profile: DeviceProfile, paneWidth: number, ratio: number): PageScale | null {
  const room = paneWidth - 2 * PAGE_GUTTER;
  return room > 0 ? sized(profile, room, ratio) : null;
}

/** Z1–Z3, Z5: a page `room` CSS pixels wide at most (a positive width), on a screen of `ratio`. */
function sized(profile: DeviceProfile, room: number, ratio: number): PageScale {
  const dpr = Number.isFinite(ratio) && ratio > 0 ? ratio : 1;
  // Z1, Z2: as wide as the room, at most the page's own size.
  const css = Math.min(room, profile.width * CSS_PER_POINT);
  // Z3: rounded down, so the page never outgrows the room or its own size.
  const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, Math.floor((css / profile.width) * dpr * SCALE_STEPS) / SCALE_STEPS));
  return { scale, width: (profile.width * scale) / dpr, height: (profile.height * scale) / dpr };
}

/** Z6 (part 15a): spreads when the room holds two pages side by side at this share of their own size or more. */
export const SPREAD_SHARE = 0.4;
/** Z7: CSS pixels kept under the pages for their captions. */
export const CAPTION_ROOM = 32;

/** The book view's pages as the pane shows them: spreads or one page, and each page's scale and size. */
export interface BookScale extends PageScale {
  readonly mode: BookMode;
}

/**
 * Z6–Z8: the book view in a pages area `width` × `height` CSS pixels: two facing pages when the room holds them at
 * {@link SPREAD_SHARE} of their size, else one; each as large as the room's width (half of it for a spread) and its
 * height less the captions allow, at most its own size, rounded as Z3. A height of zero or less (not known, or no room
 * under the bar) leaves the width alone to decide. Null with no width to draw in (Z4, Z8).
 */
export function bookScale(profile: DeviceProfile, width: number, height: number, ratio: number): BookScale | null {
  const room = width - 2 * PAGE_GUTTER;
  if (!(room > 0)) return null;
  const own = profile.width * CSS_PER_POINT;
  const mode: BookMode = room >= 2 * own * SPREAD_SHARE ? 'spread' : 'page';
  const tall = height - 2 * PAGE_GUTTER - CAPTION_ROOM;
  const across = mode === 'spread' ? room / 2 : room;
  const fits = tall > 0 ? Math.min(across, (tall * profile.width) / profile.height) : across;
  return { ...sized(profile, fits, ratio), mode };
}
